import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Newspaper,
    Pin,
    Flame,
    AlertTriangle,
    Megaphone,
    Info,
    Quote,
    ChevronRight,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "./ConfirmDialog";

const NEWS_CATEGORY_META = {
    urgence: { label: "URGENCE", color: "#ef4444", bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.4)", Icon: Flame },
    alerte: { label: "ALERTE", color: "#fb923c", bg: "rgba(251,146,60,0.15)", border: "rgba(251,146,60,0.4)", Icon: AlertTriangle },
    campagne: { label: "CAMPAGNE", color: "#a78bfa", bg: "rgba(167,139,250,0.15)", border: "rgba(167,139,250,0.4)", Icon: Megaphone },
    temoignage: { label: "TÉMOIGNAGE", color: "#fbbf24", bg: "rgba(251,191,36,0.15)", border: "rgba(251,191,36,0.4)", Icon: Quote },
    info: { label: "INFO", color: "#7dd3fc", bg: "rgba(125,211,252,0.15)", border: "rgba(125,211,252,0.4)", Icon: Info },
};

function formatRelativeDate(iso) {
    if (!iso) return "";
    try {
        const dt = new Date(iso);
        const now = new Date();
        const diffH = (now - dt) / 3600000;
        if (diffH < 1) return "à l'instant";
        if (diffH < 24) return `il y a ${Math.floor(diffH)} h`;
        const diffD = Math.floor(diffH / 24);
        if (diffD < 7) return `il y a ${diffD} j`;
        return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    } catch {
        return "";
    }
}

/**
 * Dashboard card — aggregates the 4-5 most recent news items across all
 * associations the recruter has access to. Click on a card → opens the
 * association detail page with the news tab pre-selected.
 */
export default function NewsTerrainCard() {
    const [items, setItems] = useState(null); // null = loading
    const { user } = useAuth();
    const confirm = useConfirm();
    const isAdmin = user?.role === "admin";

    useEffect(() => {
        api.get("/me/news/feed?limit=5")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    }, []);

    /**
     * Iter-30.23 — Suppression d'une actualité directement depuis le dashboard
     * (admin only). Confirme, supprime via DELETE /admin/news/{nid}, et retire
     * la card de la liste optimistically. Propagation à tous les comptes
     * automatique (doc supprimé en DB → feed mis à jour pour tous).
     */
    const handleDelete = async (e, news) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isAdmin) return;
        const ok = await confirm({
            title: "Supprimer cette actualité ?",
            description: `« ${news.title.slice(0, 80)} »\n\nL'actualité sera retirée pour TOUS les ambassadeurs (toutes assos confondues). Action irréversible.`,
            confirmText: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/news/${news.news_id}`);
            setItems((prev) => prev.filter((n) => n.news_id !== news.news_id));
            toast.success("Actualité supprimée", {
                description: "Elle disparaît pour tous les comptes.",
            });
        } catch (err) {
            toast.error("Échec de la suppression — réessaie");
        }
    };

    if (items === null) {
        return (
            <div className="glass-card rounded-3xl p-6 h-32 animate-pulse" />
        );
    }
    if (items.length === 0) {
        return null; // hide card if no news at all
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="glass-card rounded-3xl p-5 lg:p-6"
            data-testid="dashboard-news-terrain-card"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                        <Newspaper className="w-5 h-5 text-shariti-orange" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            Actualités terrain
                        </p>
                        <h3 className="text-base lg:text-lg font-extrabold text-white">
                            Quoi de neuf chez tes assos
                        </h3>
                    </div>
                </div>
                <Link
                    to="/associations"
                    className="text-[11px] font-bold text-shariti-orange-light hover:text-shariti-orange flex items-center gap-1 whitespace-nowrap shrink-0"
                    data-testid="news-terrain-see-all"
                >
                    Toutes les assos
                    <ChevronRight className="w-3 h-3" />
                </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.slice(0, 5).map((n, idx) => {
                    const meta = NEWS_CATEGORY_META[n.category] || NEWS_CATEGORY_META.info;
                    const Icon = meta.Icon;
                    return (
                        <Link
                            key={n.news_id}
                            to={`/associations/${n.association_id}?tab=news`}
                            className={`group relative rounded-2xl border transition-all hover:scale-[1.01] overflow-hidden ${
                                n.pinned
                                    ? "border-shariti-orange/30 bg-gradient-to-br from-shariti-orange/8 to-transparent"
                                    : !n.is_read
                                      ? "border-red-400/25 bg-red-500/3"
                                      : "border-white/8 bg-white/3"
                            }`}
                            data-testid={`news-terrain-item-${idx}`}
                        >
                            {/* Iter-30.24 — Bouton corbeille admin-only avec
                                stratégie d'affichage adaptée :
                                - Mobile / touch : visible toujours (lg:opacity-0)
                                  car pas de hover sur écran tactile
                                - Desktop : opacity-0 au repos puis opacity-100
                                  au hover (cohérent avec le reste du dashboard)
                                + DOUBLE PROTECTION anti-suppression accidentelle :
                                  1. Dialogue de confirmation avec le titre exact
                                  2. Bouton bien isolé visuellement (rouge), bord
                                     marqué, click event stopPropagation pour ne
                                     pas déclencher la navigation vers la news. */}
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={(e) => handleDelete(e, n)}
                                    data-testid={`news-terrain-admin-delete-${idx}`}
                                    aria-label="Supprimer cette actualité"
                                    title="Supprimer (admin) — propagé à tous les comptes"
                                    className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-red-500/20 hover:bg-red-500/40 border border-red-400/40 text-red-300 hover:text-red-100 flex items-center justify-center transition-all opacity-70 lg:opacity-0 lg:group-hover:opacity-100 active:scale-90 backdrop-blur-sm"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            )}
                            {/* Iter-30.25 — Image hero de l'actu (OG-fetched par Lia
                                ou uploadée par l'asso). Affichée en bandeau full-width
                                au-dessus du contenu si présente. Hauteur fixe pour
                                garder l'alignement de la grille. Fallback gracieux :
                                si pas d'image_url, la card commence directement par le
                                contenu (look actuel préservé pour les news sans image). */}
                            {n.image_url && (
                                <div className="relative w-full h-32 bg-shariti-deep-black overflow-hidden">
                                    <img
                                        src={n.image_url}
                                        alt=""
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        onError={(e) => {
                                            // Si l'image cassée (404, CORS...), on cache proprement
                                            e.currentTarget.parentElement.style.display = "none";
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-shariti-deep-black/80 via-shariti-deep-black/0 to-transparent pointer-events-none" />
                                </div>
                            )}
                            <div className="p-3.5">
                            <div className={`flex items-center gap-2 mb-2 flex-wrap ${isAdmin ? "pr-9" : ""}`}>
                                <span
                                    className="text-[8px] font-extrabold tracking-[0.2em] uppercase px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0"
                                    style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
                                >
                                    <Icon className="w-2.5 h-2.5" />
                                    {meta.label}
                                </span>
                                {/* Iter-30.28 — Nom de l'asso JAMAIS tronqué.
                                    On retire truncate/max-w pour laisser le chip
                                    wrap sur sa propre ligne si nécessaire grâce
                                    au flex-wrap parent. Tracking réduit pour
                                    gagner de la place sur les cartes étroites. */}
                                <span
                                    className="text-[9px] font-extrabold tracking-[0.08em] uppercase px-1.5 py-0.5 rounded-full bg-white/8 text-white/85 border border-white/12 break-words leading-tight"
                                    data-testid={`news-terrain-asso-${idx}`}
                                    title={n.association_name || "Shariti"}
                                >
                                    {n.association_name || "Shariti"}
                                </span>
                                {n.pinned && (
                                    <Pin className="w-3 h-3 text-shariti-orange-light shrink-0" />
                                )}
                                {!n.is_read && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse ml-auto shrink-0" />
                                )}
                            </div>
                            <p className="text-[12px] font-extrabold text-white leading-tight line-clamp-2 mb-1.5">
                                {n.title}
                            </p>
                            <div className="flex items-center justify-end text-[10px] text-white/45 font-light">
                                <span>{formatRelativeDate(n.published_at)}</span>
                            </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </motion.div>
    );
}
